import React, { useEffect, useState } from "react";
import { Box, Button, List, ListItem, ListItemText, Typography, TextField, Grid, Card, CardContent, Paper, Dialog } from "@mui/material";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../Context/Contexts";
import { BatchOrderViewModel, BatchSummaryViewModel, ListQuery } from "../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import AddOrderToBatchWizard from "../../components/Wizards/AddOrderToBatchWizard";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import ResponsivePagination from "../../components/ResponsivePagination";

const BatchEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { batchId = 0 } = params || {};
    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const { navigate } = useNavigation();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [batch, setBatch] = useState<BatchSummaryViewModel | null>(null);
    const [orders, setOrders] = useState<BatchOrderViewModel[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useCachedListQuery({ page: 0, pageSize: 10, filter: "", filters: [] });
    const [wizardOpen, setWizardOpen] = useState(false);

    const fetchBatch = async () => {
        if (!batchId) return;
        const res = await authService.get<BatchSummaryViewModel>(`/batchApi/Batch?batchId=${batchId}`);
        setBatch({ ...res, originalValues: { ...res } });
    };

    const fetchOrders = async () => {
        if (!batchId) return;
        showLoading();
        try {
            const result = await authService.post<{ items: BatchOrderViewModel[]; count: number }>(`/batchApi/GetOrders?batchId=${batchId}`, query);
            setOrders(result.items);
            setTotal(result.count);
        } finally {
            hideLoading();
        }
    };

    useEffect(() => {
        fetchBatch();
    },

        [batchId]);

    useEffect(() => {
        fetchOrders();
    },
        [batchId, query]);

    const openPick = (orderNo?: string | null) => {
        if (!orderNo) return;
        navigate({ path: "warehouse/pick/edit", params: { orderNo } });
    };

    const openPack = (orderNo?: string | null) => {
        if (!orderNo) return;
        navigate({ path: "warehouse/pack/edit", params: { orderNo } });
    };

    const openWizard = () => setWizardOpen(true);

    const removeOrder = async (orderNo?: string | null) => {
        if (!orderNo) return;
        const isConfirmed = window.confirm(t("dialog.deleteRowConfirm"));
        if (!isConfirmed) return;
        showLoading();
        try {
            await authService.post("/batchApi/RemoveOrder", { batchId, orderNo });
            fetchOrders();
        } finally {
            hideLoading();
        }
    };

    const cancelWizard = () => setWizardOpen(false);

    const handleOrderAdded = () => {
        fetchOrders();
    };

    const updateDescription = async () => {
        if (!batch || batch.systemBatch) return;
        await authService.post("/batchApi/SetDescription", { batchId, description: batch.batchDescription });
    };

    const handleCancelChanges = () => {
        //setIsLoading(true);
        try {
            setBatch((prev) => {
                if (!prev || !prev.originalValues) return prev;

                const original = prev.originalValues;
                const changes: Partial<BatchSummaryViewModel> = {};


                for (const key in original) {
                    const typedKey = key as keyof BatchSummaryViewModel;
                    const originalValue = original[typedKey];
                    const currentValue = prev[typedKey];

                    if (originalValue !== currentValue) {
                        changes[typedKey] = originalValue;
                    }
                }

                 if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };

                return {
                    ...prev,
                    ...changes,
                    hasChanges:false
                };
            });
        } catch (error: any) {
            console.error("Failed to cancel changes:", error);
            showSnackbar(`${t("common.failedToCancelChanges")} : ${error.message}`, "error");
        } finally {
            // setIsLoading(false);
        }
    };

    return (
        <>
            <Dialog open={wizardOpen} maxWidth="sm" fullWidth onClose={cancelWizard}>
                <Box p={3}>
                    <AddOrderToBatchWizard batchId={batchId} onAdded={handleOrderAdded} cancelWizard={cancelWizard} />
                </Box>
            </Dialog>
            <CenteredCard title="Batch" isLoading={isLoading} handleSave={updateDescription} handleCancel={handleCancelChanges} hasChanges={batch?.hasChanges === true}>

            <Paper sx={{ padding: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6">
                        Batch: {batchId}
                    </Typography>
                    <TextField
                        label={t("common.description")}
                            value={batch?.batchDescription ?? ""}
                            onChange={e => setBatch(b => b ? { ...b, batchDescription: e.target.value, hasChanges:true } : b)}
                        fullWidth
                      
                        minRows={4}
                        sx={{ width: "100%", maxWidth: 600,mb:5 }}
                        disabled={batch?.systemBatch === true}
                    />
                    <Button variant="contained" onClick={openWizard}>{t("common.addOrderToBatch")}</Button>
                </Box>
                <List>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {orders.map((order) => (
                            <Grid item
                                xs={12}
                                sm={orders.length === 1 ? 12 : orders.length === 2 ? 6 : 6}
                                md={orders.length === 1 ? 12 : orders.length === 2 ? 6 : 4}
                                key={order.orderNo}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                            {order.orderNo}
                                        </Typography>
                                        <Typography variant="body2">
                                            {t("common.order")}: {order.basketNo}
                                        </Typography>

                                        <Box sx={{ marginTop: 2, textAlign: "center" }}>
                                            <Button sx={{ mb: 2 }} variant="contained" color="info" size="small" onClick={() => openPick(order.orderNo)}>{t("common.openPick")}</Button>
                                            <Button sx={{ mb: 2 }} variant="contained" color="success" size="small" onClick={() => openPack(order.orderNo)}>{t("common.openPack")}</Button>
                                            <Button variant="contained" color="error" size="small" onClick={() => removeOrder(order.orderNo)}>{t("common.delete")}</Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        {orders.length === 0 && (
                            <Grid item xs={12}>
                                <Typography align="center">{t("common.noEntriesFound")}</Typography>
                            </Grid>
                        )}
                    </Grid>

                </List>
                <ResponsivePagination
                    page={query.page}
                    pageSize={query.pageSize}
                    totalCount={total}
                    onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
                    sx={{ mt: 2 }}
                />
            </Paper>
            </CenteredCard>
        </>
    );
};

export default BatchEditPage;
